import React from "react";

import { emptyObject, emptyFunc, merge } from "../utils/utils";

function useFields(options = {}) {
  const {
    initialValues = emptyObject,
    initialTouched = emptyObject,
    validate = emptyFunc,
  } = options;

  const [fields, setFields] = React.useState(() => {
    return {
      values: initialValues,
      touched: initialTouched,
      errors: {},
    };
  });

  const handleChange = React.useCallback((updaterOrFields) => {
    setFields((prevFields) => {
      let nextFields = updaterOrFields;
      if (typeof updaterOrFields === "function") {
        nextFields = updaterOrFields(prevFields);
      }
      if (nextFields === prevFields) {
        return prevFields;
      }
      return nextFields;
    });
  }, []);

  const setValues = React.useCallback((values) => {
    setFields((prevFields) => {
      if (prevFields.values === values) {
        return prevFields;
      }
      return {
        ...prevFields,
        values,
      };
    });
  }, []);

  const setTouched = React.useCallback((touched) => {
    setFields((prevFields) => {
      if (prevFields.touched === touched) {
        return prevFields;
      }
      return {
        ...prevFields,
        touched,
      };
    });
  }, []);

  const reset = React.useCallback(() => {
    setFields((prevFields) => {
      return {
        values: initialValues,
        touched: initialTouched,
        errors: prevFields.errors,
      };
    });
  }, [initialValues, initialTouched]);

  const result = React.useMemo(() => {
    const errors = merge(validate(fields.values), fields.errors);
    const valid = !fields.errors || Object.keys(fields.errors).length === 0;
    return {
      fields: {
        ...fields,
        errors,
      },
      valid,
      handleChange,
      reset,
      setValues,
      setTouched,
    };
  }, [fields, validate, handleChange, reset, setTouched, setValues]);

  return result;
}

export default useFields;
